import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { pick } from 'lodash-es';
import { Observable, catchError, throwError } from 'rxjs';

import { Logger } from '@mobi/rwc-utils-ng-jslib';

import { SESSION_STORAGE } from '../shared/constants/storage.constants';
import { fatalErrorOccurred } from '../state/actions/fatal-error.actions';
import { State } from '../state/reducers/fatal-error.reducer';

import { SKIP_GLOBAL_FATAL_ERROR } from './http-context-tokens';

@Injectable({
    providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler, HttpInterceptor {
    private logger = inject(Logger);
    private injector = inject(Injector);

    handleError(error: any): void {
        const ignoreDispatch =
            error instanceof DOMException ||
            this.ignoreHttpError(error) ||
            this.ignoreError(error) ||
            this.ignoreIsTrustedError(error);

        this.logError(error, ignoreDispatch ? 'warn' : 'error');

        if (!ignoreDispatch) {
            const store = this.getStore();
            store.dispatch(
                fatalErrorOccurred({
                    fatalError: this.createFatalErrorPayload(error),
                }),
            );
        }
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error?.status === 401) {
                    this.logError(error, 'error'); // prevent redirect
                } else if (req.context.get(SKIP_GLOBAL_FATAL_ERROR)) {
                    // failure is handled gracefully by the caller
                    this.logError(error, 'warn', { sessionId: this.getSessionId() });
                } else {
                    this.handleError(error);
                }
                return throwError(() => error);
            }),
        );
    }

    private logError(exception: any, level: 'warn' | 'error', extra: Record<string, unknown> = {}) {
        const msg = exception?.message || exception;
        const corrId = exception?.headers?.get?.('x-corr-id');
        const additionalProperties = {
            ...extra,
            exception:
                exception instanceof Error || exception instanceof HttpErrorResponse
                    ? exception
                    : undefined,
            corrId,
        };

        // If the message is generic or missing, serialize the whole object for better diagnostics.
        if (!exception?.message && typeof exception === 'object' && exception !== null) {
            try {
                const errorDetails = JSON.stringify(
                    exception,
                    Object.getOwnPropertyNames(exception),
                );
                this.logger[level](`Generic error caught. Details: ${errorDetails}`, {
                    ...extra,
                    corrId,
                });
                return;
            } catch (e) {
                this.logger.error(`Error during exception serialization: ${e?.message || e}`, {
                    corrId,
                });
                return;
            }
        }

        this.logger[level](msg, additionalProperties);
    }

    // The conversation id that also tags backend requests; present in both the public and
    // authenticated zones, absent (null) before a conversation has been started.
    private getSessionId(): string | null {
        return sessionStorage.getItem(SESSION_STORAGE.CONVERSATION_ID.key);
    }

    private ignoreHttpError(error: any) {
        const matchingIgnoreRule =
            error instanceof HttpErrorResponse && error.url
                ? IGNORE_HTTP_ERRORS.find(
                      rule => rule.ignoreStatus(error.status) && rule.regex.test(error.url),
                  )
                : undefined;
        return !!matchingIgnoreRule;
    }

    private ignoreError(error: any) {
        const ignore = IGNORE_ERRORS.find(i => i.ignore(error));
        if (ignore) {
            this.logger.warn(
                `ignoring error rule=${ignore.rule} message=${error?.message} stack: ${error?.stack}`,
            );
            return true;
        }
        return false;
    }

    private getStore(): Store {
        return this.injector.get(Store); // break cyclic dependency on store when using devtools
    }

    private ignoreIsTrustedError(error: any) {
        if (error && typeof error !== 'string' && error.isTrusted === true && !error.message) {
            this.logger.warn('ignoring client side error');
            return true;
        }
        return false;
    }

    private createFatalErrorPayload(error: any): State {
        const pickedError =
            typeof error === 'string'
                ? error
                : pick(error, 'message', 'stack', 'name', 'url', 'status', 'statusText', 'error');

        if (typeof pickedError.error === 'object' && pickedError.error !== null) {
            pickedError.error = pick(pickedError.error, 'message', 'stack', 'name');
        }

        return {
            error: pickedError,
            type: 'caught-by-global-error-handler',
            reloadPageMightHelp: false,
        };
    }
}

const IGNORE_HTTP_ERRORS: { regex: RegExp; ignoreStatus: (httpStatus: number) => boolean }[] = [
    {
        regex: /\/mcs\/schadenmeldung\/rest\/api\/schadenmeldung\/v2\/users\/current/,
        ignoreStatus: status => status === 403, // when lot 2
    },
];
const IGNORE_ERRORS: { rule: string; ignore: (err: any) => boolean }[] = [
    {
        rule: 'ignore-errors-from-googlemaps',
        ignore: err => /at .*https:\/\/maps.googleapis.com\/maps\/api/.test(err?.stack),
    },
];
